import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { tap } from "rxjs";
import { AssetModel } from "../models/asset.model";
import { AdminService } from "../services/admin.service";
import {
  GetAssetAdminAction,
  GetSelectedAssetAdminAction,
  AddAssetAdminAction,
  DeleteAssetAdminAction,
  FilterAssetAdminAction,
} from "./asset-admin.actions";

export interface AssetAdminStateModel {
  selectedAsset: AssetModel | null;
  allAssets: AssetModel[];
  filteredAssets: AssetModel[];
}

@State<AssetAdminStateModel>({
  name: "AssetAdminStore",
  defaults: {
    selectedAsset: null,
    allAssets: [],
    filteredAssets: [],
  },
})
@Injectable()
export class AssetAdminState {
  constructor(private service: AdminService) {}

  @Selector()
  static selectAssets(state: AssetAdminStateModel) {
    return state.filteredAssets;
  }

  @Selector()
  static selectSelectedAsset(state: AssetAdminStateModel) {
    return state.selectedAsset;
  }

  @Selector()
  static selectAllAssets(state: AssetAdminStateModel) {
    return state.allAssets;
  }

  @Action(GetAssetAdminAction)
  getAssetAdminAction(ctx: StateContext<AssetAdminStateModel>) {
    return this.service.getAllAssets().pipe(
      tap((assetsFetched: AssetModel[]) => {
        ctx.patchState({ 
          allAssets: assetsFetched || [],
          filteredAssets: assetsFetched || []
        });
      })
    );
  }

  @Action(GetSelectedAssetAdminAction)
  getSelectedAssetAdminAction(
    ctx: StateContext<AssetAdminStateModel>,
    action: GetSelectedAssetAdminAction
  ) {
    return this.service.getAssetById(action.id).pipe(
      tap((assetFetched: AssetModel) => {
        ctx.patchState({ selectedAsset: assetFetched });
      })
    );
  }

  @Action(AddAssetAdminAction)
  addAssetAdminAction(
    ctx: StateContext<AssetAdminStateModel>,
    action: AddAssetAdminAction
  ) {
    return this.service.createAsset(action.assetData).pipe(
      tap((newAsset: AssetModel) => {
        const currentState = ctx.getState();
        ctx.patchState({
          allAssets: [...currentState.allAssets, newAsset],
          filteredAssets: [...currentState.filteredAssets, newAsset]
        });
      })
    );
  }

  @Action(DeleteAssetAdminAction)
  deleteAssetAdminAction(
    ctx: StateContext<AssetAdminStateModel>,
    action: DeleteAssetAdminAction
) {
    if (action.assetType === 'FILE') {
        return this.service.deleteAssetFromMinio(action.resource).pipe(
            tap(() => {
                const currentState = ctx.getState();
                const updatedAssets = currentState.allAssets.filter(asset => asset.id !== action.id);
                ctx.patchState({
                    allAssets: updatedAssets,
                    filteredAssets: updatedAssets
                });
            })
        );
    } else {
        return;
    }
}

  @Action(FilterAssetAdminAction)
  filterAssetAdminAction(ctx: StateContext<AssetAdminStateModel>, action: FilterAssetAdminAction) {
    const allAssets = ctx.getState().allAssets;
    ctx.patchState({
      filteredAssets: allAssets.filter(asset => 
        (!action.filter.name || asset.name.toLowerCase().includes(action.filter.name.toLowerCase())) &&
        (!action.filter.type || asset.type === action.filter.type)
      )
    });
  }
}